import { currencyFormat } from '@client/global/constants';
import { safeLocalStorage } from '@client/utils/storage/safe-storage';
import { LOCAL_STORAGE_KEY } from '@shared/constants';
import numeral from 'numeral';
import type { DependencyList } from 'react';
import rfdc from 'rfdc';

/**
 * Creates a Promise that resolves after the specified number of milliseconds
 *
 * @param ms - The number of milliseconds to sleep
 * @returns A Promise that resolves after the specified delay
 *
 * @throws {Error} If ms is negative or not a finite number
 *
 * @example
 * ```typescript
 * // Sleep for 1 second
 * await sleep(1000);
 *
 * // Sleep for 500ms between API calls
 * for (const item of items) {
 *   await processItem(item);
 *   await sleep(500);
 * }
 * ```
 */
export function sleep(ms: number): Promise<void> {
    // Input validation
    if (!Number.isFinite(ms) || ms < 0) {
        throw new Error(`Sleep duration must be a non-negative finite number, got: ${ms}`);
    }

    return new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
    });
}

export function logInDev(...data: unknown[]) {
    if (import.meta.env.DEV) {
        console.log(...data);
    } else {
        const enableDevLog = safeLocalStorage.getItem(LOCAL_STORAGE_KEY.ENABLE_DEV_INFO);
        if (enableDevLog) {
            console.log(...data);
        }
    }
}

export const flattenJSON = function (
    data: unknown,
    delimiter: string = '.',
    keyTransformer: (input: string) => string = (input) => input,
) {
    const result: Record<string, unknown> = {};
    const recurse = (cur: any, prop: string) => {
        if (Object(cur) !== cur) {
            result[prop] = cur;
        } else if (Array.isArray(cur)) {
            let l, i;
            for (i = 0, l = cur.length; i < l; i++) recurse(cur[i], prop + '.' + i);
            if (l === 0) result[prop] = [];
        } else {
            let isEmpty = true;
            for (const p in cur) {
                isEmpty = false;
                recurse(cur[p], prop ? prop + '.' + p : p);
            }
            if (isEmpty && prop) result[prop] = {};
        }
    };
    recurse(data, '');
    const cleanResult: Record<string, unknown> = {};
    Object.keys(result).forEach((key) => {
        cleanResult[
            key
                .split('.')
                .map((e) => keyTransformer(e))
                .join(delimiter)
        ] = result[key];
    });
    return cleanResult;
};

export function formatCurrency(
    number: number | null | undefined,
    options?: { emptyValue?: string; withSymbol?: boolean },
) {
    const _options = { ...{ emptyValue: '-', withSymbol: true }, ...options };
    if (number === null || number === undefined) {
        return _options.emptyValue;
    }
    return numeral(number).format(_options.withSymbol ? currencyFormat.withSymbol : currencyFormat.withoutSymbol);
}

export function cloneDeep<T = unknown>(obj: T): T {
    return rfdc()(obj);
}

export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number = 500,
): (...funcArgs: Parameters<T>) => Promise<ReturnType<T>> {
    let timeout: NodeJS.Timeout | null = null;
    let latestResolve: ((value: ReturnType<T> | PromiseLike<ReturnType<T>>) => void) | null = null;
    let latestReject: ((reason?: any) => void) | null = null;

    return (...args: Parameters<T>) => {
        return new Promise<ReturnType<T>>((resolve, reject) => {
            // Cancel previous timeout if it exists
            if (timeout) {
                clearTimeout(timeout);
                // Silently ignore the previous pending call instead of rejecting
                // This prevents "Debounced call superseded" errors
                latestResolve = null;
                latestReject = null;
            }

            // Store the latest resolve/reject functions
            latestResolve = resolve;
            latestReject = reject;

            timeout = setTimeout(async () => {
                try {
                    const result = await func(...args);
                    if (latestResolve) {
                        latestResolve(result);
                    }
                } catch (error) {
                    if (latestReject) {
                        latestReject(error);
                    }
                } finally {
                    // Clean up
                    latestResolve = null;
                    latestReject = null;
                    timeout = null;
                }
            }, wait);
        });
    };
}

export function areDependenciesEqual(prevDeps: DependencyList | undefined, nextDeps: DependencyList) {
    if (!prevDeps || prevDeps.length !== nextDeps.length) {
        return false;
    }

    return prevDeps.every((item, i) => Object.is(item, nextDeps[i]));
}

export const SortOrder = {
    asc: 'asc',
    desc: 'desc',
} as const;

export type SortOrder = (typeof SortOrder)[keyof typeof SortOrder];

export const NullsOrder = {
    first: 'first',
    last: 'last',
} as const;

export type NullsOrder = (typeof NullsOrder)[keyof typeof NullsOrder];

export type SortOrderInput = {
    sort: SortOrder;
    nulls?: NullsOrder;
};
